// cSpell:ignore Bonheiden boîte Scenariolaan Zilverberklaan
// Who a settlement statement is from and to (Lewis, 2026-10-19): the office
// of the settled property as sender, never its owner, and the addressee with
// a postal address a window envelope shows. The lines are frozen into the
// statement in the recipient's language ("bus 2", "boîte 2"; a country other
// than Belgium by its name).

import {
  getLocalizedText,
  toLocale,
} from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { IAddressParts, unitBoxLabel } from "./groupUnits.utils";
import { IPayerCandidate } from "./payer.utils";

// The postal address of a contact, as its fields hold it.
export interface IPostalAddress {
  street?: string;
  number?: string;
  box?: string;
  zip?: string;
  city?: string;
  countryId?: string;
}

// The office of the settled property, the statements' sender.
export interface IStatementOffice {
  name: string;
  address?: IPostalAddress;
}

// The country the offices write from: its name is left off an address.
const HOME_COUNTRY = "BE";

const trimmed = (value?: string | null): string => value?.trim() ?? "";

// A contact's address when it can carry a letter: a street, and a postal
// code or a city.
export const postalAddressOf = (contact?: {
  street?: string | null;
  number?: string | null;
  box?: string | null;
  zip?: string | null;
  city?: string | null;
  countryId?: string | null;
}): IPostalAddress | undefined => {
  if (!contact) return undefined;
  const address = {
    street: trimmed(contact.street),
    number: trimmed(contact.number),
    box: trimmed(contact.box),
    zip: trimmed(contact.zip),
    city: trimmed(contact.city),
    countryId: trimmed(contact.countryId),
  };
  return address.street && (address.zip || address.city) ? address : undefined;
};

// "Zilverberklaan 2 bus B", "2820 Bonheiden", and the country when it is not
// Belgium: the box and the country in `locale`.
export const postalAddressLines = (
  address: IPostalAddress | undefined,
  locale?: string,
): string[] => {
  if (!address) return [];
  const box = trimmed(address.box);
  const street = [trimmed(address.street), trimmed(address.number)]
    .filter(Boolean)
    .join(" ");
  const country = trimmed(address.countryId).toUpperCase();
  return [
    box ? `${street} ${unitBoxLabel({ code: box, box }, locale)}` : street,
    [trimmed(address.zip), trimmed(address.city)].filter(Boolean).join(" "),
    country && country !== HOME_COUNTRY
      ? getLocalizedText(
          `system.country.${country.toLowerCase()}`,
          {},
          0,
          toLocale(locale),
        )
      : "",
  ].filter(Boolean);
};

// The leased unit's address, where its tenants live: "Scenariolaan 1 bus 2",
// "9000 Gent". None when its street is not known.
export const unitPostalLines = (
  unit:
    | { code: string; box?: string; addressParts?: IAddressParts }
    | undefined,
  locale?: string,
): string[] => {
  const parts = unit?.addressParts;
  if (!unit || !parts) return [];
  const box = unit.box?.trim();
  return [
    box
      ? `${parts.street} ${unitBoxLabel({ code: unit.code, box }, locale)}`
      : parts.street,
    parts.city ?? "",
  ].filter(Boolean);
};

// Where a lease's statement is posted: the address of the payer the broker
// chose (or the one guessed for them), the person whose language the letter
// is in (decision 9 of 2026-10-20); without one on file, the first of its
// tenants that has one (a lease without tenants: of its parents). Undefined
// when none of them has one: the statement then goes to the leased unit.
export const leasePostalAddress = (params: {
  candidates: IPayerCandidate[];
  addressOf: (accountId: number) => IPostalAddress | undefined;
  payerId?: number;
}): IPostalAddress | undefined => {
  const { candidates, addressOf, payerId } = params;
  const payerAddress = payerId != null ? addressOf(payerId) : undefined;
  if (payerAddress) return payerAddress;
  const tenants = candidates.filter(candidate => candidate.role === "tenant");
  const addressees =
    tenants.length > 0
      ? tenants
      : candidates.filter(candidate => candidate.role === "parent");
  for (const addressee of addressees) {
    const address = addressOf(addressee.accountId);
    if (address) return address;
  }
  return undefined;
};

// The office of the settled property as the statements' sender: the company
// on its contact, else the office's name, and its address.
export const statementOfficeOf = (params: {
  broker?: { name?: string; accountId?: number } | null;
  contact?: { company?: string | null; address?: IPostalAddress };
}): IStatementOffice | undefined => {
  const { broker, contact } = params;
  const name = trimmed(contact?.company) || trimmed(broker?.name);
  if (!broker || !name) return undefined;
  return { name, ...(contact?.address ? { address: contact.address } : {}) };
};

// Who the statements of a settlement are from and to: the office, and per
// lease and per owner the postal address on file.
export interface IStatementAddresses {
  office?: IStatementOffice;
  byContractId: Record<string, IPostalAddress | undefined>;
  ownerByKey: Record<string, IPostalAddress | undefined>;
}
